export type StatusReuniao =
  | 'AGENDADA'
  | 'CONFIRMADA'
  | 'REALIZADA'
  | 'CANCELADA'
  | 'REAGENDADA'

/** Formato da reunião (Onda 3): espelha `entity/FormatoReuniao`. */
export type FormatoReuniao = 'ONLINE' | 'PRESENCIAL'

/**
 * Transições permitidas a partir de cada status — espelha
 * `StatusReuniao.podeTransitarPara` do backend (que é a autoridade; aqui só
 * evitamos oferecer no Select uma transição que ele recusaria):
 * AGENDADA → CONFIRMADA → REALIZADA é o caminho feliz; AGENDADA → REALIZADA
 * continua válida; REALIZADA e CANCELADA são terminais; mesmo → mesmo é
 * reenvio idempotente, não transição — por isso o status atual sempre entra.
 */
export const transicoesValidas = (atual: StatusReuniao): StatusReuniao[] => {
  switch (atual) {
    case 'AGENDADA':
      return ['AGENDADA', 'CONFIRMADA', 'REALIZADA', 'CANCELADA', 'REAGENDADA']
    case 'CONFIRMADA':
      return ['CONFIRMADA', 'REALIZADA', 'CANCELADA', 'REAGENDADA']
    case 'REAGENDADA':
      return ['REAGENDADA', 'AGENDADA', 'CONFIRMADA', 'REALIZADA', 'CANCELADA']
    case 'REALIZADA':
      return ['REALIZADA']
    case 'CANCELADA':
      return ['CANCELADA']
  }
}

export interface Reuniao {
  id: string
  consultorId: string
  consultorNome?: string
  oportunidadeId?: string
  clienteId?: string
  clienteNome?: string
  titulo: string
  descricao?: string
  local?: string
  /** ONLINE | PRESENCIAL. Ausente em reuniões anteriores à Onda 3. */
  formato?: FormatoReuniao | null
  dataInicio: string
  dataFim: string
  status: StatusReuniao
  observacoes?: string
  /** Marca a reunião como o primeiro contato com o cliente (Etapa 3 da jornada). */
  primeiroContato?: boolean
  /** Quem esteve na reunião — texto livre: a sala inclui gente de fora da plataforma. */
  participantes?: string
  createdAt: string
  updatedAt: string
}

export interface ReuniaoRequest {
  oportunidadeId?: string | null
  clienteId?: string | null
  clienteNome?: string | null
  titulo: string
  descricao?: string | null
  local?: string | null
  /**
   * ONLINE | PRESENCIAL. O PUT é full-replace: SEMPRE envie o formato atual ao
   * editar, senão a edição apaga o campo em silêncio (condição de integração do
   * A06/R01). PRESENCIAL exige `local` — validado no backend.
   */
  formato?: FormatoReuniao | null
  dataInicio: string // ISO 8601
  dataFim: string
  status?: StatusReuniao | null
  observacoes?: string | null
  primeiroContato?: boolean | null
  participantes?: string | null
}

export interface AtualizarStatusReuniaoRequest {
  status: StatusReuniao
  observacoes?: string | null
}

/**
 * Uma linha da worklist de agendamentos pendentes do Operador (Etapa 2 da jornada de
 * análise: "Identificação de reuniões pendentes").
 *
 * Espelha `dto/AgendamentoPendenteResponse` — confira campo a campo ao mexer.
 */
export interface AgendamentoPendente {
  oportunidadeId: string
  numero?: number
  cnpj?: string
  razaoSocial?: string
  status: string
  consultorId?: string
  enviadaEm?: string
  /** Há quanto tempo a reserva espera pelo agendamento — ordena a lista. */
  diasDesdeEnvio: number
  lembretesEnviados: number
  ultimoLembreteEm?: string
}

/** Espelha `dto/LembreteAgendamentoResponse` — histórico do agendamento. */
export interface LembreteAgendamento {
  id: string
  oportunidadeId: string
  consultorId: string
  enviadoPor: string
  enviadoPorNome?: string
  mensagem?: string
  enviadoEm: string
}
